import type { SampleToolResultSectionGradingEntry } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import { sectionIsNotApplicableValueToDisplayValue } from "@repo/app-types/helpers/recordingAssessmentFeedbackHelpers";
import { Size } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";
import { GradingsTableContactCell } from "../../features/teammatePerformance/gradingsTableContactCell.js";
import { TeammateNameStack } from "../../features/teammatePerformance/teammateNameStack.js";

interface SectionSampleGradingsTableProps {
  entries: readonly SampleToolResultSectionGradingEntry[];
  isLoading?: boolean;
  isError?: boolean;
}

type SectionSampleGradingRow = SampleToolResultSectionGradingEntry & {
  id: string;
};

const applicabilityLabel = (
  grade: { is_not_applicable: boolean } | null | undefined,
): string =>
  grade
    ? sectionIsNotApplicableValueToDisplayValue(grade.is_not_applicable)
    : "—";

export const SectionSampleGradingsTable: FC<SectionSampleGradingsTableProps> = (
  props,
) => {
  const { entries } = props;

  const rows = useMemo(
    (): SectionSampleGradingRow[] =>
      entries.map((e) => ({ ...e, id: e.grading.id })),
    [entries],
  );

  const columns = useMemo(
    (): EntityTableColumnDefinition<SectionSampleGradingRow>[] => [
      {
        id: "customer",
        className: "w-[28%]",
        header: "customer",
        isRowHeader: true,
        renderCell: (entry) => (
          <GradingsTableContactCell grading={entry.grading} />
        ),
      },
      {
        id: "teammate",
        className: "w-[20%]",
        header: "Teammate",
        renderCell: (entry) => <TeammateNameStack teammate={entry.teammate} />,
      },
      {
        id: "currentGrade",
        className: "w-0",
        header: "Current",
        renderCell: (entry) => applicabilityLabel(entry.baseline_grade),
      },
      {
        id: "testGrade",
        className: "w-0",
        header: "Test",
        renderCell: (entry) => applicabilityLabel(entry.tested_grade),
      },
      {
        id: "testCommentary",
        className: "w-[50%]",
        header: "Test commentary",
        renderCell: (entry) => (
          <div>{entry.tested_grade?.commentary ?? "—"}</div>
        ),
      },
    ],
    [],
  );

  return (
    <EntityTable
      aria-label="Graded calls"
      columns={columns}
      entities={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      emptyMessage="No graded calls in this sample."
      size={Size.SMALL}
    />
  );
};
